// ===========================================
// Admin Layout Component
// ===========================================
// Provides consistent layout for admin pages with sidebar navigation

import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Collapse,
  Badge,
  Chip,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import { BottomNavigation } from './BottomNavigation';
import AdminRouteGuard from './AdminRouteGuard';
import {
  Menu as MenuIcon,
  Dashboard,
  CalendarMonth,
  LocationOn,
  MeetingRoom,
  MusicNote,
  Category,
  Timer,
  Person,
  FamilyRestroom,
  School,
  Logout,
  Settings,
  Handshake,
  LibraryMusic,
  Receipt,
  Notifications,
  EventAvailable,
  Celebration,
  FactCheck,
  LocalOffer,
  EventBusy,
  Folder,
  Groups,
  ExpandLess,
  ExpandMore,
  Business,
  Tune,
  People,
  Event,
  Work,
  NoteAlt,
  TrendingUp,
  AttachMoney,
  Email,
  History,
  AccountBalanceWallet,
  HowToReg,
  Security,
  Autorenew,
  Feed,
  RssFeed,
  Extension,
  EmojiEvents,
  LibraryBooks,
  Leaderboard,
  ShowChart,
  Storefront,
  ImportExport,
  AltRoute,
  Assessment,
  Campaign,
  Repeat,
  QuestionAnswerOutlined as QuestionAnswerOutlinedIcon,
  Sms,
  CalendarToday as SaveTheDateIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/useAuth';
import { useStudentView } from '../../contexts/useStudentView';
import { useAdminTeacherView } from '../../contexts/useAdminTeacherView';
import {
  bottomNavFor,
  layoutReadGates,
  pathViewSection,
  resolveEffectiveView,
  studentViewActive,
  viewSwitcherOptions,
} from './effectiveView';
import { useStudentLayout } from '../../contexts/StudentLayoutContext';
import { useMeetAndGreetCounts } from '../../hooks/useMeetAndGreet';
import { useOnboardingConfig } from '../../hooks/useAdminSettings';
import { useOnboardingCounts } from '../../hooks/useOnboarding';
import { useMyStudentProfile, useUpdateStudentAvatar } from '../../hooks/useStudentProfile';
import { useMyChildren } from '../../hooks/useParentProfile';
import { useUnreadCount } from '../../hooks/useMessages';
import { useAdminNavigation, useLegalSettings } from '../../hooks/useAdminSettings';
import { useMyAvatar } from '../../hooks/useAvatar';
import { useParentUnpaidInvoiceCount } from '../../hooks/useInvoices';
import { usePendingPermissionSlipCount } from '../../hooks/usePermissionSlips';
import { AvatarDisplay } from '../avatar';
import { type UserRole } from '../../types/avatar.types';
import { useRoleColorScheme } from '../../hooks/useRoleColorScheme';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';
import { StudentAvatar, AvatarPickerDialog } from '../student';
import {
  Face as FaceIcon,
  ChildCare as ChildCareIcon,
  SwapHoriz as SwapHorizIcon,
  ExitToApp as ExitToAppIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  School as TeacherIcon,
  Close as CloseIcon,
  ManageAccounts as ManageAccountsIcon,
  Badge as BadgeIcon,
  FamilyRestroom as FamilyRestroomViewIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../hooks/usePermissions';
import { filterAdminNavigation } from './filterAdminNavigation';

const DRAWER_WIDTH = 260;
// The phone drawer overlays the page, so it can be wider than the desktop sidebar: wide enough that
// the longest labels ("SMS & WhatsApp Campaigns") stay on one line (Andrew, 27/10/2026).
const MOBILE_DRAWER_WIDTH = 'min(300px, 85vw)';
/** A nav label never wraps on a phone; on the narrowest one it ends in an ellipsis instead. */
const NAV_LABEL_TYPOGRAPHY = {
  fontSize: '0.9rem',
  sx: { whiteSpace: { xs: 'nowrap', md: 'normal' }, overflow: { xs: 'hidden', md: 'visible' }, textOverflow: { xs: 'ellipsis', md: 'clip' } },
} as const;

interface NavigationItem {
  label: string;
  path: string;
  icon: typeof Dashboard;
}

interface NavigationSection {
  id: string;
  label: string;
  icon: typeof Dashboard;
  items: NavigationItem[];
}

interface NavigationConfig {
  standalone: NavigationItem[];
  sections: NavigationSection[];
}

// Navigation items for admin sidebar
// IMPORTANT: When adding/removing items or sections here, also update NAV_ITEMS in
// components/admin/settings/NavigationSettingsTab.tsx and the backend validNavItemIds
// in validators/adminSettings.validators.ts to keep them aligned.
const adminNavigationConfig: NavigationConfig = {
  standalone: [
    { label: 'Dashboard', path: '', icon: Dashboard },
    { label: 'Messages', path: 'messages', icon: Email },
    { label: 'Calendar', path: 'calendar', icon: CalendarMonth },
    { label: 'Meet & Greet', path: 'meet-and-greet', icon: Handshake },
    { label: 'Enquiries', path: 'enquiries', icon: QuestionAnswerOutlinedIcon },
  ],
  sections: [
    {
      id: 'events',
      label: 'Events',
      icon: Celebration,
      items: [
        { label: 'Events', path: 'events', icon: Celebration },
        { label: 'Save the Dates', path: 'save-the-dates', icon: SaveTheDateIcon },
        { label: 'Permission Slips', path: 'permission-slips', icon: FactCheck },
        // Inherits the `events` section permission (see getFilteredAdminConfig) —
        // no itemPermissionMap entry and no validNavItemIds change needed for a
        // CHILD item. (sectionIdMap looks sections up by `id` since 2026-09-16, so
        // adding or removing a SECTION no longer shifts the ones after it.)
        { label: 'Pricing Structures', path: 'event-pricing-structures', icon: LocalOffer },
      ],
    },
    {
      id: 'school-setup',
      label: 'School Setup',
      icon: Business,
      items: [
        { label: 'Terms', path: 'terms', icon: CalendarMonth },
        { label: 'Locations', path: 'locations', icon: LocationOn },
        { label: 'Rooms', path: 'rooms', icon: MeetingRoom },
      ],
    },
    {
      id: 'class-config',
      label: 'Class Configuration',
      icon: Tune,
      items: [
        { label: 'Age Groups', path: 'age-groups', icon: Groups },
        { label: 'Durations', path: 'durations', icon: Timer },
        { label: 'Instruments', path: 'instruments', icon: MusicNote },
        { label: 'Class Types', path: 'class-types', icon: Category },
        { label: 'Program Levels', path: 'program-levels', icon: TrendingUp },
        { label: 'Streams', path: 'streams', icon: AltRoute },
        { label: 'Piece Library', path: 'pieces', icon: LibraryBooks },
      ],
    },
    {
      id: 'gamification',
      label: 'Gamification',
      icon: EmojiEvents,
      items: [
        { label: 'Achievements', path: 'achievements', icon: EmojiEvents },
        { label: 'Student Progress', path: 'student-progress', icon: ShowChart },
        { label: 'Leaderboard', path: 'leaderboard', icon: Leaderboard },
      ],
    },
    {
      id: 'people',
      label: 'People',
      icon: People,
      items: [
        { label: 'Teachers', path: 'teachers', icon: Person },
        { label: 'Families', path: 'families', icon: FamilyRestroom },
        { label: 'Students', path: 'students', icon: School },
        // Enquiries Phase 6: gated on student_onboarding by ITEM_PERMISSION_MAP (a child item).
        { label: 'Onboarding check-in', path: 'settling-in', icon: FactCheck },
      ],
    },
    {
      id: 'scheduling',
      label: 'Scheduling',
      icon: Event,
      items: [
        { label: 'Classes', path: 'classes', icon: LibraryMusic },
        { label: 'Extra Lesson Requests', path: 'extra-lessons', icon: Category },
      ],
    },
    {
      id: 'operations',
      label: 'Operations',
      icon: Work,
      items: [
        { label: 'Invoices', path: 'invoices', icon: Receipt },
        { label: 'Subscriptions', path: 'subscriptions', icon: Autorenew },
        { label: 'Class Pricing', path: 'pricing-config', icon: AttachMoney },
        { label: 'Products', path: 'products', icon: Storefront },
        { label: 'Payment Settings', path: 'payment-settings', icon: Settings },
      ],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: Assessment,
      items: [
        { label: 'Overview', path: 'reports', icon: Dashboard },
        { label: 'Attendance', path: 'reports/attendance', icon: Assessment },
        { label: 'Revenue', path: 'reports/revenue', icon: AttachMoney },
        { label: 'Outstanding Invoices', path: 'reports/outstanding-invoices', icon: Receipt },
        { label: 'Student Progress', path: 'reports/student-progress', icon: School },
        { label: 'Teacher Performance', path: 'reports/teacher-performance', icon: Person },
        { label: 'Hybrid Booking Analytics', path: 'reports/hybrid-booking', icon: CalendarMonth },
        { label: 'Enrollment Analytics', path: 'reports/enrollment', icon: TrendingUp },
        { label: 'Operations', path: 'reports/operations', icon: Work },
        { label: 'Retro Edits', path: 'reports/retro-edits', icon: History },
        // #642 Phase 2 Request 8: owner only (OWNER_ONLY_ITEMS, OWNER_ONLY_SUBPATHS)
        { label: 'Family Credit', path: 'reports/family-credit', icon: AccountBalanceWallet },
        // #374 Phase 2: gated by registration_fees.read, NOT by `reports` (ITEM_PERMISSION_MAP)
        { label: 'Registration fees not applied', path: 'reports/registration-fees', icon: HowToReg },
        // Inherits the section's `reports` permission gate. A reports.read-only
        // staff user can open this page and is refused the save — intended.
        { label: 'Report settings', path: 'reports/settings', icon: Settings },
      ],
    },
    {
      id: 'content',
      label: 'Communications',
      icon: Feed,
      items: [
        { label: 'Newsletters', path: 'newsletters', icon: RssFeed },
        { label: 'Custom Emails', path: 'custom-emails', icon: Campaign },
        { label: 'Email Sequences', path: 'email-sequences', icon: Repeat },
        { label: 'SMS & WhatsApp Campaigns', path: 'custom-sms', icon: Sms },
        { label: 'System SMS/WhatsApp', path: 'sms-settings', icon: Sms },
        { label: 'System Emails', path: 'system-emails', icon: Email },
        { label: 'Email Log', path: 'email-log', icon: Email },
        { label: 'Email Settings', path: 'email-settings', icon: Settings },
      ],
    },
    // The 'website' section (Pages / Custom Blocks / Blog Posts / Website Settings)
    // was removed 2026-09-16 with the website builder (md/plan/website-removal.md).
    {
      id: 'system',
      label: 'System',
      icon: AdminPanelSettingsIcon,
      items: [
        { label: 'Roles & Permissions', path: 'roles', icon: ManageAccountsIcon },
        { label: 'Integrations', path: 'integrations', icon: Extension },
        { label: 'Import / Export', path: 'import-export', icon: ImportExport },
        { label: 'Security Audit', path: 'security-audit', icon: Security },
        { label: 'System Settings', path: 'settings', icon: Settings },
      ],
    },
  ],
};

// Navigation items for parent sidebar (simple structure - no sections needed)
const parentNavigationConfig: NavigationConfig = {
  standalone: [
    { label: 'Dashboard', path: '', icon: Dashboard },
    { label: 'Messages', path: 'messages', icon: Email },
    { label: 'Bookings', path: 'bookings', icon: EventAvailable },
    { label: 'Report Absence', path: 'absences', icon: EventBusy },
    { label: 'Invoices', path: 'invoices', icon: Receipt },
    { label: 'Resources', path: 'resources', icon: Folder },
    { label: 'Calendar', path: 'calendar', icon: CalendarMonth },
    { label: 'Events', path: 'events', icon: Celebration },
    { label: 'Permission Slips', path: 'permission-slips', icon: FactCheck },
    { label: 'Enrolments', path: 'enrolments', icon: School },
    { label: 'Family Info', path: 'family-info', icon: FamilyRestroom },
  ],
  sections: [],
};

// Navigation items for teacher sidebar (simple structure - no sections needed)
const teacherNavigationConfig: NavigationConfig = {
  standalone: [
    { label: 'Dashboard', path: '', icon: Dashboard },
    { label: 'Calendar', path: 'calendar', icon: CalendarMonth },
    { label: 'Teacher Tasks', path: 'teacher-tasks', icon: NoteAlt },
    { label: 'Classes', path: 'classes', icon: LibraryMusic },
    { label: 'Files', path: 'files', icon: Folder },
    { label: 'Messages', path: 'messages', icon: Email },
  ],
  sections: [],
};

// Navigation items for student sidebar (minimal - read-only access)
const studentNavigationConfig: NavigationConfig = {
  standalone: [
    { label: 'Dashboard', path: '', icon: Dashboard },
    { label: 'Messages', path: 'messages', icon: Email },
  ],
  sections: [],
};

// Map nav item IDs to the navigation structure.
//
// ⚠️ Sections are looked up by their `id`, standalone items by their `path` —
// NEVER by array position. This used to be `sections[9]`-style indexing, so
// removing one section (website, 2026-09-16) would have silently pointed
// `system` at the wrong entry. A lookup that fails yields `undefined`, which
// `applyNavigationSettings` skips — a typo here drops an item rather than
// mislabelling it.
const standaloneByPath = (path: string): NavigationItem | undefined =>
  adminNavigationConfig.standalone.find((item) => item.path === path);
const sectionById = (id: string): NavigationSection | undefined =>
  adminNavigationConfig.sections.find((section) => section.id === id);

const sectionIdMap: Record<string, { standalone?: NavigationItem; section?: NavigationSection }> = {
  dashboard: { standalone: standaloneByPath('') },
  messages: { standalone: standaloneByPath('messages') },
  calendar: { standalone: standaloneByPath('calendar') },
  meetAndGreet: { standalone: standaloneByPath('meet-and-greet') },
  enquiries: { standalone: standaloneByPath('enquiries') },
  events: { section: sectionById('events') },
  'school-setup': { section: sectionById('school-setup') },
  'class-config': { section: sectionById('class-config') },
  gamification: { section: sectionById('gamification') },
  people: { section: sectionById('people') },
  scheduling: { section: sectionById('scheduling') },
  operations: { section: sectionById('operations') },
  reports: { section: sectionById('reports') },
  content: { section: sectionById('content') },
  system: { section: sectionById('system') },
};

// Get all valid navigation item IDs
const allNavItemIds = ['dashboard', 'messages', 'calendar', 'meetAndGreet', 'enquiries', 'events', 'school-setup', 'class-config', 'gamification', 'people', 'scheduling', 'operations', 'reports', 'content', 'system'];

/**
 * Apply saved navigation settings to the admin navigation config
 * Reorders and filters items based on saved preferences
 * New items not in saved order will be appended at the end
 */
function applyNavigationSettings(
  order: string[] | undefined,
  hidden: string[] | undefined
): NavigationConfig {
  // If no custom order, use default
  const savedOrder = order && order.length > 0 ? order : allNavItemIds;
  const hiddenSet = new Set(hidden || []);

  // Find any new items that aren't in the saved order and append them
  const savedOrderSet = new Set(savedOrder);
  const newItems = allNavItemIds.filter(id => !savedOrderSet.has(id));
  const effectiveOrder = [...savedOrder, ...newItems];

  const standalone: NavigationItem[] = [];
  const sections: NavigationSection[] = [];

  for (const id of effectiveOrder) {
    // Skip hidden items
    if (hiddenSet.has(id)) continue;

    const mapping = sectionIdMap[id];
    if (mapping?.standalone) {
      standalone.push(mapping.standalone);
    } else if (mapping?.section) {
      sections.push(mapping.section);
    }
  }

  return { standalone, sections };
}

export default function AdminLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  // The user menu's role chip: the school's role colour (#629) and the owner title, never "Admin".
  const roleScheme = useRoleColorScheme();
  const ownerTitle = useOwnerTitle();
  const {
    isStudentView: storedStudentView,
    selectedStudent,
    enterStudentView,
    exitStudentView,
    switchStudent,
  } = useStudentView();
  // A parent's "student view" applies on the parent pages only (whole-slice /qa F-M1).
  const onParentPages = pathViewSection(location.pathname) === 'parent';
  const isStudentView = studentViewActive(location.pathname, storedStudentView);
  const { currentView, setView, availableViews } = useAdminTeacherView();
  const { layoutMode } = useStudentLayout();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [studentMenuAnchor, setStudentMenuAnchor] = useState<null | HTMLElement>(null);
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);

  // Permissions
  const { can, canAny, isOwner } = usePermissions();

  // User role checks
  const isStudent = user?.role === 'STUDENT';
  const isParent = user?.role === 'PARENT';
  // hasDualAccess: true when the view switcher should show (2+ views available)
  const hasDualAccess = availableViews.length > 1;

  // Check if student is in fun mode (hide navigation)
  // Also suppress layout when a parent is viewing a student in fun mode
  const isStudentFunMode = (isStudent || (isParent && isStudentView)) && layoutMode === 'fun';
  const { data: studentProfile } = useMyStudentProfile({ enabled: isStudent });
  const updateAvatar = useUpdateStudentAvatar();
  const { data: myAvatar } = useMyAvatar();

  // Fetch children for parent's student view toggle
  const { data: children = [] } = useMyChildren({ enabled: isParent });

  // The four admin reads below feed only the admin/staff navigation (sidebar and bottom-nav badge):
  // they run on the admin pages, and only for a caller who may read them (#292, #272). Teachers,
  // parents and students never send them.
  const readGates = layoutReadGates(location.pathname, can);

  // Pending meet & greet counts for the badge (the route needs meet_and_greet.read)
  const { data: meetAndGreetCounts } = useMeetAndGreetCounts({ enabled: readGates.meetAndGreetCounts });
  const pendingMeetAndGreets = (meetAndGreetCounts?.PENDING_APPROVAL ?? 0) + (meetAndGreetCounts?.PENDING_VERIFICATION ?? 0);

  // Onboarding config decides M&G vs New Students nav (system_settings.read; unloaded = M&G)
  const { data: onboardingConfigResponse } = useOnboardingConfig({ enabled: readGates.onboardingConfig });
  const useMeetAndGreetNav = onboardingConfigResponse?.data?.requireMeetAndGreet !== false;

  // Onboarding counts for the New Students badge when M&G is off (meet_and_greet.read)
  const { data: onboardingCounts } = useOnboardingCounts({ enabled: readGates.onboardingCounts });
  const pendingOnboarding = (onboardingCounts?.awaitingApproval ?? 0) + (onboardingCounts?.inProgress ?? 0);

  // Saved admin navigation order/visibility (system_settings.read; unloaded = default order)
  const { data: navSettings } = useAdminNavigation({ enabled: readGates.navigation });

  // Fetch unread message count for badge
  const { data: unreadCountData } = useUnreadCount();
  const unreadMessages = unreadCountData?.count ?? 0;

  // Fetch unpaid invoice count for badge (only for parents)
  const { count: unpaidInvoices } = useParentUnpaidInvoiceCount({ enabled: isParent });

  // Pending permission slips for the badge (parents only). Derived from the
  // list the portal page already loads — no extra endpoint for a number.
  const { count: pendingSlips } = usePendingPermissionSlipCount({ enabled: isParent });

  // Fetch legal settings for sidebar footer links
  const { data: legalData } = useLegalSettings();

  // Load collapsed sections from localStorage
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('adminNavCollapsed');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Save collapsed sections to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('adminNavCollapsed', JSON.stringify(Array.from(collapsedSections)));
  }, [collapsedSections]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    const schoolSlug = extractSchoolSlugFromPath(location.pathname);
    navigate(schoolSlug ? `/${schoolSlug}/login` : '/login');
  };

  // Navigation is handled by component={Link} on ListItemButtons
  // Mobile drawer close is handled inline in onClick

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Permission-filtered admin navigation for staff (logic + owner-only items: ./filterAdminNavigation.ts).
  const getFilteredAdminConfig = (base: NavigationConfig): NavigationConfig =>
    filterAdminNavigation(base, { isStaff: !!user?.isStaff, role: user?.role, canAny, isOwner });

  // Derive the effective view from the current URL path so the nav always
  // reflects where the user actually is, not just their last explicit switch.
  // The decision lives in ./effectiveView.ts (pure, unit-tested) — see the
  // 2026-09-16 note there: /admin used to map to 'admin' unconditionally, which
  // handed a PARENT/TEACHER with a custom staff role the wrong sidebar.
  const getEffectiveView = (): typeof currentView =>
    resolveEffectiveView(location.pathname, currentView, user);

  // The page the user is ON decides the view. The stored view (and so the profile menu's
  // "Viewing as" / "Switch to") follows it: a staff teacher logs in to /teacher while the
  // stored view still says Staff, and the menu offered "Switch to Teacher View" for the page
  // they were already on (2026-10-11).
  const routeView = getEffectiveView();
  // The phone's bottom nav follows the same view (roles 1b §5.15).
  const bottomNav = bottomNavFor(routeView, { role: user?.role ?? 'ADMIN', isStaff: user?.isStaff });
  useEffect(() => {
    if (routeView !== currentView && availableViews.includes(routeView)) setView(routeView);
  }, [routeView, currentView, availableViews, setView]);

  // Get navigation config based on user role (or view mode)
  const getNavigationConfig = (): NavigationConfig => {
    // If parent is in student view mode, show simplified student navigation
    // Note: Messages removed since students without accounts can't have their own messages
    if (isParent && isStudentView) {
      return {
        standalone: [
          { label: 'Dashboard', path: '', icon: Dashboard },
        ],
        sections: [],
      };
    }

    const effectiveView = getEffectiveView();

    // currentView drives the active navigation when multiple views are available
    if (effectiveView === 'teacher') {
      return teacherNavigationConfig;
    }

    if (effectiveView === 'parent') {
      return parentNavigationConfig;
    }

    // staff view: admin routes with permission-filtered navigation
    if (effectiveView === 'staff') {
      const base = navSettings
        ? applyNavigationSettings(navSettings.order, navSettings.hidden)
        : adminNavigationConfig;
      return getFilteredAdminConfig(base);
    }

    // admin view (or legacy role-based fallback)
    switch (user?.role) {
      case 'PARENT':
        return parentNavigationConfig;
      case 'TEACHER':
        return teacherNavigationConfig;
      case 'STUDENT':
        return studentNavigationConfig;
      case 'ADMIN':
      default:
        // Apply saved navigation settings for admins
        if (navSettings) {
          return applyNavigationSettings(navSettings.order, navSettings.hidden);
        }
        return adminNavigationConfig;
    }
  };

  const navigationConfig = getNavigationConfig();

  // Get the base path for the current role (or view mode)
  const getBasePath = (): string => {
    const schoolSlug = extractSchoolSlugFromPath(location.pathname);
    const slugPrefix = schoolSlug ? `/${schoolSlug}` : '';

    if (currentView === 'teacher') return `${slugPrefix}/teacher`;
    if (currentView === 'parent') return `${slugPrefix}/parent`;
    // staff view reuses admin routes — nav is filtered by permissions
    if (currentView === 'staff') return `${slugPrefix}/admin`;

    switch (user?.role) {
      case 'PARENT':
        return `${slugPrefix}/parent`;
      case 'TEACHER':
        return `${slugPrefix}/teacher`;
      case 'STUDENT':
        return `${slugPrefix}/student`;
      case 'ADMIN':
      default:
        return `${slugPrefix}/admin`;
    }
  };

  const basePath = getBasePath();

  const isActivePath = (path: string) => {
    // Handle empty path (index route)
    if (path === '') {
      return location.pathname === basePath;
    }
    // Construct full path for comparison
    const fullPath = `${basePath}/${path}`;

    // Special case: reports overview should match exactly
    if (path === 'reports') {
      return location.pathname === fullPath;
    }

    return location.pathname.startsWith(fullPath);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Background music notes pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-evenly',
          p: 2,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {[...Array(8)].map((_, index) => (
          <Box
            key={index}
            component="img"
            src={index % 2 === 0 ? '/music-notes.png' : '/music-notes-3.png'}
            alt=""
            sx={{
              maxWidth: '60%',
              maxHeight: 60,
              opacity: 0.25,
              objectFit: 'contain',
              alignSelf: 'flex-end',
            }}
          />
        ))}
      </Box>

      {/* Logo */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            maxWidth: '80%',
            maxHeight: 60,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Navigation */}
      <List sx={{ px: 1, overflow: 'auto', position: 'relative', zIndex: 1, flex: 1 }}>
        {/* Render standalone items */}
        {navigationConfig.standalone.map((rawItem) => {
          // Dynamic swap: Meet & Greet -> New Students when M&G is disabled
          const item = (rawItem.label === 'Meet & Greet' && !useMeetAndGreetNav)
            ? { ...rawItem, label: 'New Students', path: 'new-students' }
            : rawItem;

          const Icon = item.icon;
          const isActive = isActivePath(item.path);
          // Show badge for Meet & Greet or New Students based on config
          const isMeetAndGreetItem = rawItem.label === 'Meet & Greet';
          const showMeetAndGreetBadge = isMeetAndGreetItem && useMeetAndGreetNav && pendingMeetAndGreets > 0;
          const showNewStudentsBadge = isMeetAndGreetItem && !useMeetAndGreetNav && pendingOnboarding > 0;
          // Show badge for Messages if there are unread messages
          const showMessagesBadge = item.label === 'Messages' && unreadMessages > 0;
          // Show badge for Invoices if there are unpaid invoices (parents only)
          const showInvoicesBadge = item.label === 'Invoices' && isParent && unpaidInvoices > 0;
          // Permission slips awaiting a signature — action required, so warning.
          const showSlipsBadge = item.label === 'Permission Slips' && isParent && pendingSlips > 0;
          const badgeCount = showMeetAndGreetBadge ? pendingMeetAndGreets : showNewStudentsBadge ? pendingOnboarding : showMessagesBadge ? unreadMessages : showInvoicesBadge ? unpaidInvoices : showSlipsBadge ? pendingSlips : 0;
          // Use brand coral color for invoice badge, warning for meet & greet / new students, error for messages
          const badgeColor = showInvoicesBadge ? undefined : (showMeetAndGreetBadge || showNewStudentsBadge || showSlipsBadge) ? 'warning' : 'error';

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path || '.'}
                onClick={() => { if (isMobile) setMobileOpen(false); }}
                selected={isActive}
                sx={{
                  borderRadius: 1,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Badge
                    badgeContent={badgeCount}
                    color={badgeColor as 'warning' | 'error' | undefined}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                        // Use brand coral color for invoice badge
                        ...(showInvoicesBadge && {
                          backgroundColor: '#FFAE9E',
                          color: '#080808',
                        }),
                      },
                    }}
                  >
                    <Icon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={NAV_LABEL_TYPOGRAPHY}
                />
              </ListItemButton>
            </ListItem>
          );
        })}

        {/* Render collapsible sections */}
        {navigationConfig.sections.map((section) => {
          const SectionIcon = section.icon;
          const isExpanded = !collapsedSections.has(section.id);

          return (
            <Box key={section.id} sx={{ mb: 1 }}>
              {/* Section Header */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => toggleSection(section.id)}
                  sx={{
                    borderRadius: 1,
                    py: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SectionIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={section.label}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                    }}
                  />
                  {isExpanded ? (
                    <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} />
                  ) : (
                    <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />
                  )}
                </ListItemButton>
              </ListItem>

              {/* Section Items */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActivePath(item.path);

                    return (
                      <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          component={Link}
                          to={item.path || '.'}
                          onClick={() => { if (isMobile) setMobileOpen(false); }}
                          selected={isActive}
                          sx={{
                            borderRadius: 1,
                            pl: { xs: 5, md: 6 },
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'primary.contrastText',
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Icon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={NAV_LABEL_TYPOGRAPHY}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      {/* School Info */}
      <Box sx={{ p: 2, position: 'relative', zIndex: 1 }}>
        {isParent && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5 }}>
            <MuiLink
              component="button"
              variant="caption"
              color="text.secondary"
              underline="hover"
              onClick={() => setLegalModal('terms')}
            >
              Terms of Service
            </MuiLink>
            <MuiLink
              component="button"
              variant="caption"
              color="text.secondary"
              underline="hover"
              onClick={() => setLegalModal('privacy')}
            >
              Privacy Policy
            </MuiLink>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" display="block">
          &copy; {new Date().getFullYear()} {user?.schoolName}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ opacity: 0.7, fontSize: '0.7rem', cursor: 'pointer' }}
          title="Click to copy"
          onClick={() => {
            navigator.clipboard?.writeText(__APP_VERSION__).catch(() => {});
          }}
        >
          v.{__APP_VERSION__}
        </Typography>
      </Box>
    </Box>
  );

  // If student is in fun mode, render only the content without navigation
  if (isStudentFunMode) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Outlet />
        {/* Avatar Picker Dialog for Students in Fun Mode */}
        {studentProfile && (
          <AvatarPickerDialog
            open={avatarPickerOpen}
            onClose={() => setAvatarPickerOpen(false)}
            currentAvatar={studentProfile.avatar}
            birthDate={studentProfile.birthDate}
            onSelect={(avatar) => {
              updateAvatar.mutate(avatar);
              setAvatarPickerOpen(false);
            }}
            isLoading={updateAvatar.isPending}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {/* User Menu */}
          <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
            {isStudent && studentProfile ? (
              <StudentAvatar
                avatar={studentProfile.avatar}
                birthDate={studentProfile.birthDate}
                firstName={studentProfile.firstName}
                lastName={studentProfile.lastName}
                size={36}
              />
            ) : (
              <AvatarDisplay
                avatarInfo={myAvatar}
                firstName={user?.firstName}
                lastName={user?.lastName}
                role={user?.role as 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT'}
                size={36}
              />
            )}
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled sx={{ display: 'flex', alignItems: 'center', gap: 1, '&.Mui-disabled': { opacity: 1 } }}>
              <Typography variant="body2">
                {user?.firstName} {user?.lastName}
              </Typography>
              {user?.role && (() => {
                const role = user.role as UserRole;
                const colors = roleScheme[role] ?? roleScheme.PARENT;
                return (
                  <Chip
                    label={role === 'ADMIN' ? ownerTitle : role.charAt(0) + role.slice(1).toLowerCase()}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: colors.background,
                      color: colors.text,
                    }}
                  />
                );
              })()}
            </MenuItem>
            <MenuItem disabled sx={{ '&.Mui-disabled': { opacity: 1 } }}>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            {!isStudentView && (
              <MenuItem component={Link} to={`${basePath}/account`} onClick={handleMenuClose}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                Account Settings
              </MenuItem>
            )}
            {!isStudentView && (
              <MenuItem component={Link} to={`${basePath}/notifications`} onClick={handleMenuClose}>
                <ListItemIcon>
                  <Notifications fontSize="small" />
                </ListItemIcon>
                Notification Preferences
              </MenuItem>
            )}
            {isStudent && studentProfile && (
              <MenuItem onClick={() => { handleMenuClose(); setAvatarPickerOpen(true); }}>
                <ListItemIcon>
                  <FaceIcon fontSize="small" />
                </ListItemIcon>
                Change Avatar
              </MenuItem>
            )}
            {/* Multi-view switcher: shown whenever the user has 2+ available views */}
            {hasDualAccess && (() => {
              const viewLabels: Record<string, string> = {
                admin: 'Admin View',
                staff: user?.staffRole?.name ? `Staff View (${user.staffRole.name})` : 'Staff View',
                teacher: 'Teacher View',
                parent: 'Parent View',
              };
              const viewIcons: Record<string, React.ReactNode> = {
                admin: <AdminPanelSettingsIcon fontSize="small" />,
                staff: <BadgeIcon fontSize="small" />,
                teacher: <TeacherIcon fontSize="small" />,
                parent: <FamilyRestroomViewIcon fontSize="small" />,
              };
              const switcher = viewSwitcherOptions(routeView, availableViews);
              const viewPaths: Record<string, string> = {
                admin: '../admin',
                staff: '../admin',
                teacher: '../teacher',
                parent: '../parent',
              };

              return (
                <>
                  <Divider />
                  <MenuItem disabled sx={{ py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Viewing as: {viewLabels[switcher.current] ?? switcher.current}
                    </Typography>
                  </MenuItem>
                  {switcher.switchTo.map((v) => (
                      <MenuItem
                        key={v}
                        onClick={() => {
                          handleMenuClose();
                          setView(v);
                          navigate(viewPaths[v] ?? '../admin');
                        }}
                      >
                        <ListItemIcon>{viewIcons[v]}</ListItemIcon>
                        Switch to {viewLabels[v] ?? v}
                      </MenuItem>
                    ))}
                </>
              );
            })()}
            {/* Student View toggle for parents */}
            {isParent && !isStudentView && onParentPages && children.length > 0 && (
              <>
                <Divider />
                {children.length === 1 ? (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      enterStudentView(children[0]);
                      navigate(`${basePath}`);
                    }}
                  >
                    <ListItemIcon>
                      <ChildCareIcon fontSize="small" />
                    </ListItemIcon>
                    Student View ({children[0].firstName})
                  </MenuItem>
                ) : (
                  <MenuItem
                    onClick={(e) => {
                      setStudentMenuAnchor(e.currentTarget);
                    }}
                  >
                    <ListItemIcon>
                      <ChildCareIcon fontSize="small" />
                    </ListItemIcon>
                    Student View
                    <ExpandMore sx={{ ml: 'auto' }} fontSize="small" />
                  </MenuItem>
                )}
              </>
            )}
            {/* Exit student view / Switch student for parents in student view */}
            {isParent && isStudentView && (
              <>
                <Divider />
                <MenuItem disabled sx={{ py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Viewing as: {selectedStudent?.firstName}
                  </Typography>
                </MenuItem>
                {children.length > 1 && (
                  <MenuItem
                    onClick={(e) => {
                      setStudentMenuAnchor(e.currentTarget);
                    }}
                  >
                    <ListItemIcon>
                      <SwapHorizIcon fontSize="small" />
                    </ListItemIcon>
                    Switch Student
                    <ExpandMore sx={{ ml: 'auto' }} fontSize="small" />
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    exitStudentView();
                    navigate(`${basePath}`);
                  }}
                >
                  <ListItemIcon>
                    <ExitToAppIcon fontSize="small" />
                  </ListItemIcon>
                  Exit Student View
                </MenuItem>
              </>
            )}
            {!isStudentView && <Divider />}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>

          {/* Student selection sub-menu for parents with multiple children */}
          {isParent && children.length > 1 && (
            <Menu
              anchorEl={studentMenuAnchor}
              open={Boolean(studentMenuAnchor)}
              onClose={() => setStudentMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {children.map((child) => (
                <MenuItem
                  key={child.id}
                  onClick={() => {
                    setStudentMenuAnchor(null);
                    handleMenuClose();
                    if (isStudentView) {
                      switchStudent(child);
                    } else {
                      enterStudentView(child);
                    }
                    navigate(`${basePath}`);
                  }}
                  selected={selectedStudent?.id === child.id}
                >
                  <ListItemIcon>
                    <StudentAvatar
                      avatar={child.avatar}
                      birthDate={child.birthDate}
                      firstName={child.firstName}
                      lastName={child.lastName}
                      size={24}
                    />
                  </ListItemIcon>
                  {child.firstName} {child.lastName}
                </MenuItem>
              ))}
            </Menu>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: MOBILE_DRAWER_WIDTH,
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          // On mobile the nav Box has no width, but its temporary Drawer is
          // `keepMounted`, so the 260px paper still gives that flex sibling
          // content width. With `min-width: auto` (the flex default) this
          // element then refuses to shrink below its own content and the pair
          // sums to ~672px on a 412px screen, pushing every row off-screen.
          // Pin the width on mobile and allow it to shrink. (2026-09-15)
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,
          mt: '64px',
          bgcolor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          // Add bottom padding on mobile for bottom navigation
          pb: { xs: '80px', md: 3 },
        }}
      >
        {/* Staff permission gate: the /admin parent route admits any staff
            user, and the nav filter only hides links — this enforces the
            page-level permission for direct URLs too. */}
        <AdminRouteGuard>
          <Outlet />
        </AdminRouteGuard>
      </Box>

      {/* Avatar Picker Dialog for Students */}
      {isStudent && studentProfile && (
        <AvatarPickerDialog
          open={avatarPickerOpen}
          onClose={() => setAvatarPickerOpen(false)}
          currentAvatar={studentProfile.avatar}
          birthDate={studentProfile.birthDate}
          onSelect={(avatar) => {
            updateAvatar.mutate(avatar);
            setAvatarPickerOpen(false);
          }}
          isLoading={updateAvatar.isPending}
        />
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && user?.role && (
        <BottomNavigation
          role={bottomNav.role}
          canAny={bottomNav.filtered ? canAny : undefined}
          pendingMeetAndGreets={pendingMeetAndGreets}
          unreadMessages={unreadMessages}
          isStudentView={isStudentView}
          onMoreClick={handleDrawerToggle}
        />
      )}

      {/* Legal Document Modal */}
      <Dialog
        open={legalModal !== null}
        onClose={() => setLegalModal(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {legalModal === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
          <IconButton onClick={() => setLegalModal(null)} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!legalData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
            >
              {(legalModal === 'terms' ? legalData.termsOfService : legalData.privacyPolicy) ||
                `No ${legalModal === 'terms' ? 'terms of service' : 'privacy policy'} has been published yet.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegalModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
